import { AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";

export default function Disclaimer() {
  return (
    <div data-testid="disclaimer-page">
      <SEO
        title="Disclaimer"
        description="Legal disclaimer for Asian Picks — information only, no financial advice, no guarantee of winnings. Read before using our Asian Handicap tipping service."
        path="/disclaimer"
      />
      <section className="bg-ink-950 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-emerald-700/25 blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-gold-500/15 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gold-400 font-mono uppercase tracking-widest text-xs mb-3">
            Legal
          </div>
          <h1
            data-testid="disclaimer-title"
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl"
          >
            <span className="gold-text">Disclaimer</span>
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed">
            Please read this disclaimer carefully before using Asian Picks or
            acting on any information published on this website.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-ink-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 text-ink-700 leading-relaxed">
          <div
            data-testid="disclaimer-warning"
            className="flex items-start gap-3 p-5 rounded-xl border border-red-900/30 bg-red-50 text-red-900"
          >
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <p className="text-sm">
              <span className="font-semibold">18+ only.</span> Sports betting
              carries significant financial risk. Nothing on this website is
              financial advice or a guarantee of winnings.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              1. Information only, not financial advice
            </h2>
            <p>
              All content published on asian-picks.com — including Asian
              Handicap predictions, analysis, statistics, staking suggestions
              and commentary — is provided strictly for informational and
              entertainment purposes. Nothing on this website constitutes
              financial, investment, tax or legal advice, and no fiduciary
              relationship is created between Asian Picks and any visitor,
              reader or subscriber.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              2. No guarantee of winnings
            </h2>
            <p>
              Asian Picks does not, and cannot, guarantee that any prediction
              published or delivered to subscribers will produce a winning
              wager. Past performance, including the historical win rate
              displayed on this website, is not a reliable indicator of future
              results. All picks carry a real risk of loss, and any wagers you
              choose to place based on our content are placed at your sole
              discretion and financial risk.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              3. Your responsibility
            </h2>
            <p>
              You are solely responsible for verifying that participation in
              sports betting is legal in your jurisdiction, for selecting a
              licensed and regulated bookmaker, and for managing your bankroll
              in line with your personal financial circumstances. Asian Picks
              is not a bookmaker, does not accept, place or process any
              wagers, and holds no funds on your behalf.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              4. Third-party services and links
            </h2>
            <p>
              This website may reference or link to third-party services such
              as payment providers or bookmakers. Asian Picks has no control
              over the content, policies or availability of any third-party
              service and accepts no responsibility for any loss or damage
              arising from your use of them. Any transaction you enter into
              with a third party is strictly between you and that party.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              5. Limitation of liability
            </h2>
            <p>
              To the fullest extent permitted by applicable law, Asian Picks,
              its owners, employees and contributors shall not be liable for
              any direct, indirect, incidental, consequential or exemplary
              losses — including but not limited to loss of stake, profit,
              revenue or data — arising from your access to, use of, or
              reliance on the content of this website or its subscription
              services.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              6. Problem gambling
            </h2>
            <p>
              If you feel that gambling is negatively affecting your finances,
              relationships or mental health, please stop immediately and seek
              confidential support at{" "}
              <a
                href="https://www.begambleaware.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 underline hover:text-gold-600"
              >
                BeGambleAware.org
              </a>{" "}
              or{" "}
              <a
                href="https://www.gamblingtherapy.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 underline hover:text-gold-600"
              >
                GamblingTherapy.org
              </a>
              . If you contact us regarding a subscription and mention concerns
              about problem gambling, we will decline the subscription and
              direct you to appropriate help resources.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              7. Changes to this disclaimer
            </h2>
            <p>
              We may update this disclaimer from time to time. Any changes will
              be posted on this page with a revised effective date. Your
              continued use of the website after any such change constitutes
              acceptance of the updated disclaimer.
            </p>
          </div>

          <p className="text-xs text-ink-500 font-mono pt-6 border-t border-ink-200">
            Last updated: 2023.
          </p>
        </div>
      </section>
    </div>
  );
}
